import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Gantt, ViewMode } from 'gantt-task-react';
import 'gantt-task-react/dist/index.css';
import { format, addDays } from 'date-fns';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useTheme } from '../contexts/ThemeContext';
import TaskForm from './TaskForm';
import { matchesSearch } from '../common/search';
import { getTaskTimeUnits } from '../common/taskProgress';

// Helpers extracted from gantt-task-react to format dates in the task list
const localeDateStringCache = {};
const toLocaleDateStringFactory = (locale) => (date, dateTimeOptions) => {
  const key = date.toString();
  let lds = localeDateStringCache[key];
  if (!lds) {
    lds = date.toLocaleDateString(locale, dateTimeOptions);
    localeDateStringCache[key] = lds;
  }
  return lds;
};

const dateTimeOptions = {
  weekday: 'short',
  year: 'numeric',
  month: 'long',
  day: 'numeric',
};

// Styles copied from gantt-task-react task list table
const taskListStyles = {
  taskListWrapper: '_3ZbQT',
  taskListTableRow: '_34SS0',
  taskListCell: '_3lLk3',
  taskListNameWrapper: '_nI1Xw',
  taskListExpander: '_2QjE6',
  taskListEmptyExpander: '_2TfEi'
};

const { FiCalendar, FiFolders, FiEdit2 } = FiIcons;

const mixWithWhite = (hex, amount = 0.18) => {
  const normalized = hex.replace('#', '');
  if (normalized.length !== 6) return hex;
  const rgb = [0, 2, 4].map(index => parseInt(normalized.slice(index, index + 2), 16));
  const mixed = rgb.map(channel => Math.round(channel + (255 - channel) * amount));
  return `#${mixed.map(value => value.toString(16).padStart(2, '0')).join('')}`;
};

const GanttView = ({
  tasks,
  projects,
  currentProjectId,
  onTaskUpdate,
  updateTaskDates,
  onToggleComplete,
  searchQuery,
  tags
}) => {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState(ViewMode.Day);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [editingSubId, setEditingSubId] = useState(null);
  const { darkMode } = useTheme();

  // Filter tasks by project if a project is selected
  const filteredTasks = currentProjectId
    ? tasks.filter(task => task.projectId === currentProjectId)
    : tasks;

  // Filter out tasks without dates
  const tasksWithDates = filteredTasks.filter(task => task.dueDate && task.isMultiDay);

  // Convert tasks and subtasks to Gantt chart format
  const ganttTasks = [];

  tasksWithDates.forEach(task => {
    const project = projects.find(p => p.id === task.projectId);
    const projectColor = project?.color || '#3b82f6';

    const startDate = new Date(task.dueDate);
    const endDate = task.endDate ? new Date(task.endDate) : addDays(startDate, 1);

    const barColor = mixWithWhite(projectColor, darkMode ? 0.05 : 0.18);
    const isMatch = matchesSearch(task, searchQuery, tags);
    const multiDayProgress = getTaskTimeUnits(task);

    ganttTasks.push({
      id: task.id,
      name: task.title,
      start: startDate,
      end: endDate,
      progress: task.completed ? 100 : 0,
      type: 'task',
      styles: {
        progressColor: projectColor,
        progressSelectedColor: projectColor,
        backgroundColor: barColor,
        backgroundSelectedColor: mixWithWhite(projectColor, 0.08),
        boxShadow: isMatch ? '0 0 8px 2px rgba(59,130,246,0.7)' : '0 4px 10px rgba(15,23,42,0.16)',
      },
      project: project?.name || 'Inbox',
      task: task,
      multiDayProgress
    });

    if (task.subTasks && task.subTasks.length > 0) {
      task.subTasks.forEach(st => {
        if (!st.dueDate) return;
        let subStart = new Date(st.dueDate);
        let subEnd;
        if (st.isMultiDay && st.endDate) {
          subEnd = new Date(st.endDate);
          subEnd.setDate(subEnd.getDate() + 1);
        } else {
          const [h, m] = (st.time || '00:00').split(':').map(Number);
          subStart.setHours(h, m, 0, 0);
          subEnd = new Date(subStart);
          subEnd.setMinutes(subEnd.getMinutes() + (st.duration || 0));
          const parentEnd = task.endDate ? new Date(task.endDate) : null;
          if (parentEnd && subEnd > parentEnd) subEnd.setTime(parentEnd.getTime());
        }
        const subBarColor = darkMode ? '#475569' : mixWithWhite(projectColor, 0.45);
        const subMatch = matchesSearch(st, searchQuery, tags);
        ganttTasks.push({
          id: st.id,
          name: `↳ ${st.title}`,
          start: subStart,
          end: subEnd,
          progress: st.completed ? 100 : 0,
          type: 'task',
          styles: {
            progressColor: projectColor,
            progressSelectedColor: projectColor,
            backgroundColor: subBarColor,
            backgroundSelectedColor: mixWithWhite(projectColor, 0.28),
            boxShadow: subMatch ? '0 0 8px 2px rgba(59,130,246,0.7)' : '0 2px 6px rgba(15,23,42,0.12)',
          },
          project: project?.name || 'Inbox',
          task: st,
          parentTaskId: task.id,
          parentName: task.title
        });
      });
    }
  });

  // Handle task selection in Gantt chart
  const handleTaskSelect = (task) => {
    if (task.parentTaskId) {
      const parent = tasks.find(t => t.id === task.parentTaskId);
      if (parent) {
        setEditingTask(parent);
        setEditingSubId(task.id);
        setShowTaskForm(true);
      }
    } else {
      const original = tasks.find(t => t.id === task.id);
      if (original) {
        setEditingTask(original);
        setEditingSubId(null);
        setShowTaskForm(true);
      }
    }
  };

  // Handle task update (drag and drop)
  const handleTaskChange = (task) => {
    const day = 24 * 60 * 60 * 1000;
    const durationDays = Math.max(1, Math.round((task.end - task.start) / day));
    const start = new Date(task.start);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(start.getDate() + durationDays);
    updateTaskDates(task.id, start, end);
  };

  // Custom task list that opens the edit form when clicking a task row
  const CustomTaskListTable = ({
    rowHeight,
    rowWidth,
    fontFamily,
    fontSize,
    locale,
    tasks,
    onExpanderClick,
  }) => {
    const toLocaleDateString = useMemo(
      () => toLocaleDateStringFactory(locale),
      [locale]
    );

    return (
      <div
        className={taskListStyles.taskListWrapper}
        style={{ fontFamily, fontSize }}
      >
        {tasks.map((rowTask) => {
          let expanderSymbol = '';
          if (rowTask.hideChildren === false) {
            expanderSymbol = '▼';
          } else if (rowTask.hideChildren === true) {
            expanderSymbol = '▶';
          }

          const rowClass = rowTask.parentTaskId
            ? 'bg-slate-50 dark:!bg-slate-700'
            : 'bg-slate-200 dark:!bg-slate-800';
          const highlight = matchesSearch(rowTask.task, searchQuery, tags)
            ? 'search-highlight'
            : '';

          return (
            <div
              className={`${taskListStyles.taskListTableRow} ${rowClass} ${highlight} border-b border-slate-200 dark:border-slate-600`}
              style={{ height: rowHeight }}
              key={rowTask.id + 'row'}
              onClick={() => handleTaskSelect(rowTask)}
            >
              <div
                className={taskListStyles.taskListCell}
                style={{ minWidth: rowWidth, maxWidth: rowWidth }}
                title={rowTask.name}
              >
                <div className={taskListStyles.taskListNameWrapper}>
                  <div
                    className={
                      expanderSymbol
                        ? taskListStyles.taskListExpander
                        : taskListStyles.taskListEmptyExpander
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      onExpanderClick(rowTask);
                    }}
                  >
                    {expanderSymbol}
                  </div>
                  <div className="flex items-center mr-2">
                    <input
                      type="checkbox"
                      checked={rowTask.task.completed}
                      onChange={() => onToggleComplete(rowTask.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="mr-2"
                    />
                    {rowTask.parentTaskId && (
                      <span className="text-slate-500 mr-1">↳</span>
                    )}
                      <div className="flex flex-col">
                        <div>{rowTask.name}</div>
                        {rowTask.parentName && (
                          <div className="text-xs text-slate-500">{rowTask.parentName}</div>
                        )}
                        {rowTask.multiDayProgress && (
                          <div className="mt-1 text-xs text-slate-500">
                            {rowTask.task.isMultiDay
                              ? t('tasks.multiDayProgressDay', {
                                current: rowTask.multiDayProgress.dayLabel,
                                total: rowTask.multiDayProgress.totalDays
                              })
                              : t('tasks.timeUnitsDueWindow')}
                          </div>
                        )}
                      </div>
                  </div>
                </div>
              </div>
              <div
                className={taskListStyles.taskListCell}
                style={{ minWidth: rowWidth, maxWidth: rowWidth }}
              >
                &nbsp;{toLocaleDateString(rowTask.start, dateTimeOptions)}
              </div>
              <div
                className={taskListStyles.taskListCell}
                style={{ minWidth: rowWidth, maxWidth: rowWidth }}
              >
                &nbsp;{toLocaleDateString(rowTask.end, dateTimeOptions)}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="max-w-full mx-auto">
      <div className="mb-4 flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white mb-1">{t('gantt.title')}</h1>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            {t('gantt.subtitle')}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <SafeIcon icon={FiCalendar} className="text-slate-500 dark:text-slate-400" />
          <select
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
            className="px-3 py-1.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-sm text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value={ViewMode.Hour}>{t('gantt.viewHour')}</option>
            <option value={ViewMode.QuarterDay}>{t('gantt.viewQuarterDay')}</option>
            <option value={ViewMode.HalfDay}>{t('gantt.viewHalfDay')}</option>
            <option value={ViewMode.Day}>{t('gantt.viewDay')}</option>
            <option value={ViewMode.Week}>{t('gantt.viewWeek')}</option>
            <option value={ViewMode.Month}>{t('gantt.viewMonth')}</option>
            <option value={ViewMode.Year}>{t('gantt.viewYear')}</option>
          </select>
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiFolders} className="text-slate-500 dark:text-slate-400" />
            <span className="text-slate-600 dark:text-slate-400 text-sm">
              {currentProjectId
                ? t('gantt.showingProject', { projectName: projects.find(p => p.id === currentProjectId)?.name || 'Unknown Project' })
                : t('gantt.showingAllProjects')}
            </span>
          </div>
        </div>
      </div>

      {ganttTasks.length === 0 && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 text-blue-800 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-200 rounded-lg text-sm">
          {t('gantt.noMultiDayTasks')}
        </div>
      )}

      {ganttTasks.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 rounded-xl p-8 shadow-soft border border-slate-200/60 dark:border-slate-700/60 text-center">
          <div className="text-6xl mb-4">📊</div>
          <h3 className="text-xl font-semibold text-slate-800 dark:text-white mb-2">{t('gantt.noTasks')}</h3>
          <p className="text-slate-600 dark:text-slate-400">
            {t('gantt.noTasksDescription')}
          </p>
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-soft border border-slate-200/60 dark:border-slate-700/60 h-[calc(100vh-100px)] overflow-x-auto">
          <div className={`gantt-container ${darkMode ? 'gantt-dark-mode' : ''}`}>
            <Gantt
              tasks={ganttTasks}
              viewMode={viewMode}
              listCellWidth="180px"
              columnWidth={60}
              rowHeight={50}
              barCornerRadius={9}
              barFill={62}
              todayColor={darkMode ? "#4f46e5" : "#3b82f6"}
              onDateChange={handleTaskChange}
              TaskListTable={CustomTaskListTable}
              TooltipContent={({ task }) => (
                <div className="p-3 bg-white dark:bg-slate-700 shadow-lg rounded-lg border border-slate-200 dark:border-slate-600">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-slate-800 dark:text-white">{task.name}</h3>
                    <button
                      className="p-1 text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTaskSelect(task);
                      }}
                    >
                      <SafeIcon icon={FiEdit2} />
                    </button>
                  </div>
                  <div className="text-sm text-slate-600 dark:text-slate-400">
                    <p>{t('gantt.tooltipProject')} {task.project}</p>
                    {task.parentName && <p>{t('gantt.tooltipParent')} {task.parentName}</p>}
                    <p>{t('gantt.tooltipStart')} {format(task.start, 'MMM dd, yyyy')}</p>
                    <p>{t('gantt.tooltipEnd')} {format(task.end, 'MMM dd, yyyy')}</p>
                    <p>{t('gantt.tooltipStatus')} {task.progress === 100 ? t('gantt.statusCompleted') : t('gantt.statusInProgress')}</p>
                    {task.multiDayProgress && (
                      <p>
                        {t('tasks.multiDaySubtaskProgress', {
                          completed: task.multiDayProgress.completedSubtasks,
                          total: task.multiDayProgress.totalSubtasks,
                          percent: task.multiDayProgress.workPercent
                        })}
                        {' · '}
                        <span title={t('tasks.timeUnitsTooltip', { score: task.multiDayProgress.tuScore })}>
                          {t(`tasks.timeUnits${task.multiDayProgress.tuStatus}`, {
                            score: Math.abs(task.multiDayProgress.tuScore)
                          })}
                        </span>
                      </p>
                    )}
                  </div>
                </div>
              )}
            />
          </div>
        </div>
      )}

      {/* Task Form Modal */}
      {showTaskForm && (
        <TaskForm
          task={editingTask}
          tasks={tasks}
          editingSubtaskId={editingSubId}
          projects={projects}
          currentProjectId={currentProjectId}
          onSubmit={(updatedTask) => {
            onTaskUpdate(updatedTask);
            setShowTaskForm(false);
            setEditingTask(null);
            setEditingSubId(null);
          }}
          onClose={() => {
            setShowTaskForm(false);
            setEditingTask(null);
            setEditingSubId(null);
          }}
        />
      )}
    </div>
  );
};

export default GanttView;
